import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { AlertTriangle, Ban, ShieldAlert, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

// Avviso regole per la creazione post
export const ContentRulesWarning = ({ compact = false }) => {
  const { language } = useLanguage();
  
  if (compact) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">
              {language === 'it' ? 'Regole della community' : 'Community rules'}
            </p>
            <p>
              {language === 'it' 
                ? 'Non sono ammessi contenuti sessuali, offerte di prestazioni sessuali, truffe o contenuti illegali. Le violazioni comportano la rimozione immediata dell\'account.'
                : 'Sexual content, offers of sexual services, scams or illegal content are not allowed. Violations result in immediate account removal.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
          <ShieldAlert className="h-5 w-5 text-red-600" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">
          {language === 'it' ? 'Regole della Piattaforma' : 'Platform Rules'}
        </h3>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
          <Ban className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 text-sm">
              {language === 'it' ? 'Contenuti vietati' : 'Prohibited content'}
            </p>
            <ul className="text-sm text-red-800 mt-1 space-y-1">
              <li>• {language === 'it' ? 'Contenuti sessuali o a sfondo erotico' : 'Sexual or erotic content'}</li>
              <li>• {language === 'it' ? 'Offerte di prestazioni sessuali' : 'Offers of sexual services'}</li>
              <li>• {language === 'it' ? 'Nudità o immagini esplicite' : 'Nudity or explicit images'}</li>
              <li>• {language === 'it' ? 'Truffe o richieste fraudolente' : 'Scams or fraudulent requests'}</li>
              <li>• {language === 'it' ? 'Contenuti illegali di qualsiasi tipo' : 'Illegal content of any kind'}</li>
              <li>• {language === 'it' ? 'Incitamento all\'odio o discriminazione' : 'Hate speech or discrimination'}</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl">
          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-900 text-sm">
              {language === 'it' ? 'Contenuti ammessi' : 'Allowed content'}
            </p>
            <ul className="text-sm text-emerald-800 mt-1 space-y-1">
              <li>• {language === 'it' ? 'Richieste di aiuto genuine e verificabili' : 'Genuine and verifiable help requests'}</li>
              <li>• {language === 'it' ? 'Spese mediche, educazione, emergenze' : 'Medical expenses, education, emergencies'}</li>
              <li>• {language === 'it' ? 'Foto e video appropriati della situazione' : 'Appropriate photos and videos of the situation'}</li>
              <li>• {language === 'it' ? 'Comunicazioni rispettose' : 'Respectful communications'}</li>
            </ul>
          </div>
        </div>
      </div>

      <p className="text-xs text-stone-500 bg-stone-50 p-3 rounded-lg">
        <strong>{language === 'it' ? 'Nota:' : 'Note:'}</strong>{' '}
        {language === 'it' 
          ? 'Le violazioni delle regole comportano la rimozione immediata del contenuto e la possibile sospensione permanente dell\'account senza preavviso. Tutti i contenuti sono soggetti a moderazione.'
          : 'Rule violations result in immediate content removal and possible permanent account suspension without notice. All content is subject to moderation.'}
      </p>
    </div>
  );
};

// Alert banner per la homepage/feed
export const RulesAlertBanner = () => {
  const { language } = useLanguage();
  const [dismissed, setDismissed] = React.useState(() => {
    return localStorage.getItem('rules_banner_dismissed') === 'true';
  });

  const handleDismiss = () => {
    localStorage.setItem('rules_banner_dismissed', 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <Alert className="bg-amber-50 border-amber-200 mb-6">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-900">
        {language === 'it' ? 'Rispetta le regole della community' : 'Respect community rules'}
      </AlertTitle>
      <AlertDescription className="text-amber-800">
        {language === 'it' 
          ? 'Contenuti sessuali, truffe e comportamenti inappropriati non sono tollerati e comportano la rimozione dell\'account.'
          : 'Sexual content, scams and inappropriate behavior are not tolerated and result in account removal.'}
        <button 
          onClick={handleDismiss}
          className="ml-2 text-amber-600 hover:text-amber-700 underline text-sm"
        >
          {language === 'it' ? 'Ho capito' : 'Got it'}
        </button>
      </AlertDescription>
    </Alert>
  );
};

// Checkbox di accettazione regole per la registrazione
export const RulesAcceptanceCheckbox = ({ accepted, onChange }) => {
  const { language } = useLanguage();
  
  return (
    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl border border-stone-200">
      <input
        type="checkbox"
        id="accept-rules"
        checked={accepted}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
        required
      />
      <label htmlFor="accept-rules" className="text-sm text-stone-700 cursor-pointer">
        {language === 'it' ? (
          <>
            Accetto le <a href="/terms" className="text-emerald-700 hover:underline font-medium">Regole della Community</a> e confermo che non pubblicherò contenuti sessuali, offerte di prestazioni sessuali o contenuti illegali.
          </>
        ) : (
          <>
            I accept the <a href="/terms" className="text-emerald-700 hover:underline font-medium">Community Rules</a> and confirm that I will not post sexual content, offers of sexual services, or illegal content.
          </>
        )}
      </label>
    </div>
  );
};

export default ContentRulesWarning;
